import React from "react";
import {
  Ambulance, AlertOctagon, Flame, Waves, UserSearch, Megaphone, AlertTriangle, Check,
} from "lucide-react";
import { useT } from "../lib/lang";
import forcesSecuriteEmblem from "../assets/forces-securite.png";

/**
 * Single source of truth for the 8 official emergency categories.
 *
 * `Securite` uses a real PNG emblem (Mali-flag shield with security officers)
 * for instant national-security recognition; the others use crisp Lucide icons
 * with bold per-category accent colors that also drive the Heat-Map Command
 * Center pin colors.
 *
 * Legacy categories (Police/FAMA/Terrorist/Corruption/Kidnapping/Other) are
 * intentionally NOT listed here so they no longer surface in the UI. The
 * backend still accepts them so historical alerts continue to validate.
 */
// Palette tuned for maximum perceptual distinctness on a small screen — every
// pair of hues is at least ~25° apart on the color wheel so dispatchers can
// triage at a glance. NO two categories share a red/orange/yellow band.
//
//   Securite → royal navy   (paired with custom Mali shield emblem)
//   Violence → crimson red  (danger)
//   Accident → vivid yellow (caution / warning sign)
//   Medical  → emerald green (paramedic / health — globally recognised)
//   Fire     → deep orange  (flame)
//   Flood    → sky cyan     (water)
//   Missing  → vivid purple (search / person)
//   Report   → slate gray   (neutral / suspicious activity)
export const EMERGENCY_TYPES = [
  {
    key: "Securite",
    testid: "type-securite",
    accent: "#1E3A8A",        // royal navy
    Icon: null,               // overridden by emblemSrc
    emblemSrc: forcesSecuriteEmblem,
  },
  { key: "Violence",  testid: "type-violence",  Icon: AlertTriangle, accent: "#DC2626" },  // red
  { key: "Accident",  testid: "type-accident",  Icon: AlertOctagon,  accent: "#EAB308" },  // yellow
  { key: "Medical",   testid: "type-medical",   Icon: Ambulance,     accent: "#10B981" },  // emerald green
  { key: "Fire",      testid: "type-fire",      Icon: Flame,         accent: "#EA580C" },  // deep orange
  { key: "Flood",     testid: "type-flood",     Icon: Waves,         accent: "#0EA5E9" },  // sky cyan
  { key: "Missing",   testid: "type-missing",   Icon: UserSearch,    accent: "#9333EA" },  // purple
  { key: "Report",    testid: "type-report",    Icon: Megaphone,     accent: "#64748B" },  // slate
];

// Map of key → meta for HeatmapCommandCenter and other consumers.
export const EMERGENCY_META = Object.fromEntries(
  EMERGENCY_TYPES.map(({ key, Icon, accent, emblemSrc }) => [
    key,
    { Icon, color: accent, emblemSrc },
  ])
);

// Inner icon — either the official PNG emblem or a Lucide icon.
const TypeIconBubble = ({ entry }) => {
  if (entry.emblemSrc) {
    return (
      <span
        className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
        style={{
          background: "#0F1428",
          boxShadow: "0 8px 18px -6px rgba(14,20,48,0.45)",
        }}
      >
        <img
          src={entry.emblemSrc}
          alt=""
          className="w-11 h-11 object-contain"
          draggable={false}
        />
      </span>
    );
  }
  const Icon = entry.Icon;
  return (
    <span
      className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-md text-white"
      style={{
        background: `linear-gradient(135deg, ${entry.accent} 0%, ${entry.accent}cc 100%)`,
        boxShadow: `0 8px 18px -6px ${entry.accent}80`,
      }}
    >
      <Icon className="w-7 h-7" strokeWidth={2.4} />
    </span>
  );
};

export const EmergencyTypeGrid = ({ value, onChange }) => {
  const [, labels] = useT();
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (key) => {
    if (selected.includes(key)) onChange(selected.filter((k) => k !== key));
    else onChange([...selected, key]);
  };

  return (
    <div className="grid grid-cols-2 gap-2.5" data-testid="emergency-type-grid">
      {EMERGENCY_TYPES.map((entry) => {
        const { key, testid, accent } = entry;
        const isActive = selected.includes(key);
        const meta = labels[key] || {};
        return (
          <button
            key={key}
            type="button"
            data-testid={testid}
            aria-pressed={isActive}
            onClick={() => toggle(key)}
            style={{
              borderColor: isActive ? accent : "transparent",
              boxShadow: isActive
                ? `0 12px 22px -10px ${accent}80, 0 0 0 2px ${accent}33`
                : "0 1px 4px rgba(10,17,40,0.07)",
              background: isActive
                ? `linear-gradient(180deg, ${accent}1f 0%, ${accent}08 100%)`
                : undefined,
            }}
            className={`
              group relative flex flex-col items-center justify-start gap-1.5 px-2 py-3 rounded-2xl border-2
              text-center min-h-[120px]
              transition-all duration-200 ease-out
              focus:outline-none focus-visible:ring-2 focus-visible:ring-mali-navy/40
              ${isActive
                ? "scale-[1.02]"
                : "border-transparent bg-card text-foreground hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
              }
            `}
          >
            <TypeIconBubble entry={entry} />
            <span
              className="text-[12px] font-black tracking-tight leading-tight transition-colors"
              style={{ color: isActive ? accent : undefined }}
            >
              {meta.label || key}
            </span>
            {meta.subtitle && (
              <span className="text-[9px] font-medium uppercase tracking-[0.12em] text-muted-foreground leading-tight">
                {meta.subtitle}
              </span>
            )}
            {isActive && (
              <span
                className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm"
                style={{ background: accent, color: "#fff" }}
              >
                <Check className="w-3 h-3" strokeWidth={3.5} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
